import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { MapPin, Plus, X, MagnifyingGlass, Compass } from "@phosphor-icons/react";

// Shared geo data hook
export function useGeo() {
    const [regions, setRegions] = useState([]);
    useEffect(() => {
        api.get("/radar/geo").then(({ data }) => setRegions(data.regions || [])).catch(() => {});
    }, []);
    const provincesOf = useCallback(
        (region) => regions.find((r) => r.region === region)?.provinces || [],
        [regions]
    );
    return { regions, provincesOf };
}

// Region → Province → Comune picker (reusable)
export function AreaPicker({ regions, provincesOf, value, onChange, comune = true, idPrefix = "geo" }) {
    return (
        <div className="flex flex-wrap items-center gap-2">
            <Select
                value={value.region || ""}
                onValueChange={(v) => onChange({ region: v, province: "", comune: "" })}
            >
                <SelectTrigger className="w-[190px] h-10" data-testid={`${idPrefix}-region`}>
                    <SelectValue placeholder="Regione" />
                </SelectTrigger>
                <SelectContent>
                    {regions.map((r) => (
                        <SelectItem key={r.region} value={r.region}>{r.region}</SelectItem>
                    ))}
                </SelectContent>
            </Select>

            <Select
                value={value.province || ""}
                onValueChange={(v) => onChange({ ...value, province: v, comune: "" })}
                disabled={!value.region}
            >
                <SelectTrigger className="w-[180px] h-10" data-testid={`${idPrefix}-province`}>
                    <SelectValue placeholder="Provincia" />
                </SelectTrigger>
                <SelectContent>
                    {provincesOf(value.region).map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                </SelectContent>
            </Select>

            {comune && (
                <Input
                    data-testid={`${idPrefix}-comune`}
                    value={value.comune || ""}
                    onChange={(e) => onChange({ ...value, comune: e.target.value })}
                    placeholder="Comune (opzionale)"
                    className="w-[200px] h-10"
                    disabled={!value.province}
                />
            )}
        </div>
    );
}

// National search bar used in the Radar dashboard
export function GeoSearchBar({ regions, provincesOf, value, onChange, onReset, radiusKm = 0, onRadiusChange }) {
    const active = value.region || value.province || value.comune;
    return (
        <div className="flex flex-wrap items-center gap-2" data-testid="geo-search-bar">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-gray-500 pr-1">
                <MagnifyingGlass size={14} weight="bold" /> Cerca in Italia
            </span>
            <AreaPicker regions={regions} provincesOf={provincesOf} value={value} onChange={onChange} idPrefix="search" />
            {value.comune && onRadiusChange && (
                <Select
                    value={String(radiusKm)}
                    onValueChange={(v) => onRadiusChange(Number(v))}
                >
                    <SelectTrigger className="w-[150px] h-10" data-testid="geo-radius">
                        <SelectValue placeholder="Raggio" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="0">Senza raggio</SelectItem>
                        <SelectItem value="5">Raggio 5 km</SelectItem>
                        <SelectItem value="10">Raggio 10 km</SelectItem>
                        <SelectItem value="25">Raggio 25 km</SelectItem>
                        <SelectItem value="50">Raggio 50 km</SelectItem>
                    </SelectContent>
                </Select>
            )}
            {active && (
                <button
                    onClick={onReset}
                    data-testid="geo-search-reset"
                    className="h-10 px-3 inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-black border border-gray-200 hover:border-black transition-colors"
                >
                    <X size={13} weight="bold" /> Azzera
                </button>
            )}
        </div>
    );
}

// Scope-aware monitoring picker (National Coverage Mode):
// Tutta Italia · Regione · Provincia · Comune
const SCOPES = [
    { key: "all_italy", label: "Tutta Italia" },
    { key: "region", label: "Regione" },
    { key: "province", label: "Provincia" },
    { key: "comune", label: "Comune" },
];

export function ScopedAreaPicker({ regions, provincesOf, value, onChange, idPrefix = "scope" }) {
    const [comuni, setComuni] = useState([]);
    const scope = value.scope || "province";

    useEffect(() => {
        let alive = true;
        if (scope === "comune" && value.province) {
            api.get("/radar/geo/comuni", { params: { province: value.province } })
                .then(({ data }) => { if (alive) setComuni(data.comuni || []); })
                .catch(() => { if (alive) setComuni([]); });
        } else {
            setComuni([]);
        }
        return () => { alive = false; };
    }, [scope, value.province]);

    const setScope = (key) => onChange({ scope: key, region: "", province: "", comune: "" });

    return (
        <div className="space-y-3 w-full" data-testid={`${idPrefix}-picker`}>
            <div className="inline-flex flex-wrap gap-1 p-1 bg-gray-100 rounded-md">
                {SCOPES.map((s) => (
                    <button
                        key={s.key}
                        type="button"
                        data-testid={`${idPrefix}-scope-${s.key}`}
                        onClick={() => setScope(s.key)}
                        className={`px-3 h-8 text-xs font-semibold rounded transition-colors ${
                            scope === s.key
                                ? "bg-black text-white"
                                : "text-gray-600 hover:text-black"
                        }`}
                    >
                        {s.label}
                    </button>
                ))}
            </div>

            {scope === "all_italy" ? (
                <p className="text-sm text-gray-500">
                    Monitorerai le aste reali in <strong>tutte le regioni d'Italia</strong>.
                </p>
            ) : (
                <div className="flex flex-wrap items-center gap-2">
                    <Select
                        value={value.region || ""}
                        onValueChange={(v) => onChange({ ...value, region: v, province: "", comune: "" })}
                    >
                        <SelectTrigger className="w-[190px] h-10" data-testid={`${idPrefix}-region`}>
                            <SelectValue placeholder="Regione" />
                        </SelectTrigger>
                        <SelectContent>
                            {regions.map((r) => (
                                <SelectItem key={r.region} value={r.region}>{r.region}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    {(scope === "province" || scope === "comune") && (
                        <Select
                            value={value.province || ""}
                            onValueChange={(v) => onChange({ ...value, province: v, comune: "" })}
                            disabled={!value.region}
                        >
                            <SelectTrigger className="w-[180px] h-10" data-testid={`${idPrefix}-province`}>
                                <SelectValue placeholder="Provincia" />
                            </SelectTrigger>
                            <SelectContent>
                                {provincesOf(value.region).map((p) => (
                                    <SelectItem key={p} value={p}>{p}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}

                    {scope === "comune" && (
                        <>
                            <Input
                                data-testid={`${idPrefix}-comune`}
                                value={value.comune || ""}
                                onChange={(e) => onChange({ ...value, comune: e.target.value })}
                                placeholder="Comune"
                                list={`${idPrefix}-comuni-list`}
                                className="w-[210px] h-10"
                                disabled={!value.province}
                            />
                            <datalist id={`${idPrefix}-comuni-list`}>
                                {comuni.map((c) => <option key={c} value={c} />)}
                            </datalist>
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

// "Le mie aree monitorate"
export function MonitoredAreas({ regions, provincesOf, onChanged, onPickArea }) {
    const [areas, setAreas] = useState([]);
    const [adding, setAdding] = useState(false);
    const [draft, setDraft] = useState({ scope: "province", region: "", province: "", comune: "" });
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/radar/areas");
            setAreas(data || []);
        } catch { /* ignore */ }
    }, []);

    useEffect(() => { load(); }, [load]);

    const draftValid = () => {
        const s = draft.scope || "province";
        if (s === "all_italy") return true;
        if (s === "region") return !!draft.region;
        if (s === "province") return !!(draft.region && draft.province);
        return !!(draft.region && draft.province && draft.comune);
    };

    const add = async () => {
        if (!draftValid()) {
            toast.error("Completa la selezione dell'area");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/radar/areas", draft);
            setAreas(data.areas || []);
            setAdding(false);
            setDraft({ scope: "province", region: "", province: "", comune: "" });
            if (data.status === "added") toast.success("Area aggiunta · avvio scansione reale…");
            else if (data.status === "exists") toast.info("Area già monitorata");
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        try {
            const { data } = await api.delete(`/radar/areas/${id}`);
            setAreas(data.areas || []);
            onChanged?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
    };

    return (
        <div className="border border-gray-200 bg-white p-5 mb-6" data-testid="monitored-areas">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Compass size={18} weight="fill" />
                    <h2 className="text-base font-bold">Le mie aree monitorate</h2>
                </div>
                <button
                    onClick={() => setAdding((a) => !a)}
                    data-testid="monitored-add-toggle"
                    className="h-9 px-3 inline-flex items-center gap-1.5 text-xs font-semibold border border-gray-300 hover:border-black transition-colors"
                >
                    <Plus size={14} weight="bold" /> Aggiungi area
                </button>
            </div>

            {adding && (
                <div className="flex flex-col gap-3 mb-4 pb-4 border-b border-gray-100" data-testid="monitored-add-form">
                    <ScopedAreaPicker regions={regions} provincesOf={provincesOf} value={draft} onChange={setDraft} idPrefix="area" />
                    <button
                        onClick={add}
                        disabled={saving}
                        data-testid="monitored-add-submit"
                        className="h-10 px-4 self-start bg-black text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-50 transition-colors"
                    >
                        {saving ? "Salvataggio…" : "Monitora"}
                    </button>
                </div>
            )}

            {areas.length === 0 ? (
                <p className="text-sm text-gray-500">
                    Non monitori ancora nessuna area. Scegli <strong>Tutta Italia</strong>, una
                    regione, una provincia o un comune e inizieremo a cercare aste reali per te.
                </p>
            ) : (
                <div className="flex flex-wrap gap-2">
                    {areas.filter((a) => a && (a.label || a.comune || a.province || a.region)).map((a) => (
                        <div
                            key={a.id}
                            data-testid={`area-chip-${a.id}`}
                            className="inline-flex items-center gap-2 border border-gray-300 bg-gray-50 pl-3 pr-1.5 py-1.5 group"
                        >
                            <button
                                onClick={() => onPickArea?.(a)}
                                className="inline-flex items-center gap-1.5 text-sm font-semibold hover:text-emerald-700 transition-colors"
                                title="Filtra su quest'area"
                            >
                                <MapPin size={13} weight="fill" />
                                {a.label || (a.comune ? `${a.comune} · ${a.province}` : [a.province, a.region].filter(Boolean).join(" · "))}
                            </button>
                            <button
                                onClick={() => remove(a.id)}
                                data-testid={`area-remove-${a.id}`}
                                className="w-6 h-6 inline-flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                                title="Rimuovi"
                            >
                                <X size={13} weight="bold" />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

// First-access onboarding modal
export function GeoOnboarding({ regions, provincesOf, open, onClose, onSaved }) {
    const [draft, setDraft] = useState({ scope: "province", region: "", province: "", comune: "" });
    const [saving, setSaving] = useState(false);

    const draftValid = () => {
        const s = draft.scope || "province";
        if (s === "all_italy") return true;
        if (s === "region") return !!draft.region;
        if (s === "province") return !!(draft.region && draft.province);
        return !!(draft.region && draft.province && draft.comune);
    };

    const save = async () => {
        if (!draftValid()) {
            toast.error("Completa la selezione dell'area");
            return;
        }
        setSaving(true);
        try {
            await api.post("/radar/areas", draft);
            toast.success("Perfetto! Stiamo cercando aste reali nella tua zona…");
            onSaved?.();
            onClose?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-lg" data-testid="geo-onboarding">
                <DialogHeader>
                    <DialogTitle className="font-display-black text-2xl flex items-center gap-2">
                        <Compass size={22} weight="fill" /> Dove vuoi cercare opportunità immobiliari?
                    </DialogTitle>
                    <DialogDescription className="text-sm text-gray-500">
                        Cerca in tutta Italia o restringi a una regione, provincia o comune.
                        Potrai aggiungerne altre quando vuoi.
                    </DialogDescription>
                </DialogHeader>
                <div className="py-3 space-y-4">
                    <ScopedAreaPicker regions={regions} provincesOf={provincesOf} value={draft} onChange={setDraft} idPrefix="onb" />
                </div>
                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <button
                        onClick={() => onClose?.()}
                        data-testid="geo-onboarding-skip"
                        className="h-10 px-4 text-sm font-semibold text-gray-500 hover:text-black transition-colors"
                    >
                        Salta per ora
                    </button>
                    <button
                        onClick={save}
                        disabled={saving}
                        data-testid="geo-onboarding-save"
                        className="h-11 px-6 bg-black text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-50 transition-colors"
                    >
                        {saving ? "Avvio…" : "Inizia a monitorare"}
                    </button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
